import { Button, Link, LinkProps } from '@purpur/library';
import { Icon } from '@purpur/library/icon';
import styled, { css } from 'styled-components';
import { match } from 'ts-pattern';

import { TextButton, TextButtonWithIcon } from '@telialt/common-text-button/src';
import { purpurFocus } from '@telialt/common-theme/src/mixins';
import {
    getPurpurBorder,
    getPurpurColor,
    getPurpurSpacing,
    getPurpurTypography,
    purpurMedia,
} from '@telialt/common-theme/src/purpurSelectors';

import { NegativeAttr, PlanCardModel } from './model';

export const Card = styled.article`
    display: grid;
    grid-row: span 3;
    grid-template-rows: subgrid;
    gap: 0;
`;

export const CardCap = styled.div<{ $variant: PlanCardModel.CapVariant }>`
    border-radius: ${getPurpurBorder('radiusLg')} ${getPurpurBorder('radiusLg')} 0 0;
    padding: ${getPurpurSpacing('100')} ${getPurpurSpacing('300')};
    display: flex;
    justify-content: center;
    column-gap: ${getPurpurSpacing('100')};
    height: 2.5rem;
    font-weight: ${getPurpurTypography('weightMedium')};

    ${({ $variant }) => css`
        background: ${match($variant)
            .with('dark-purple', () => getPurpurColor('backgroundInteractivePrimary'))
            .with('light-purple', () => getPurpurColor('brandCorePurple'))
            .with('red', () => getPurpurColor('backgroundInteractiveDestructive'))
            .with('green', () => getPurpurColor('teal800'))
            .with('black', () => getPurpurColor('backgroundStatusBlackFriday'))
            .otherwise(() => getPurpurColor('backgroundInteractivePrimary'))};
    `};

    ${({ $variant }) => css`
        color: ${match($variant)
            .with('red', () => getPurpurColor('functionalWhite'))
            .otherwise(() => getPurpurColor('textDefaultNegative'))};
    `};
`;

export const Body = styled.div<NegativeAttr>(({ $negative }) => {
    const $backgroundColor = $negative
        ? getPurpurColor('backgroundToneOnTonePrimary')
        : getPurpurColor('functionalWhite');

    return css`
        position: relative;
        z-index: 1;
        flex-grow: 1;
        display: flex;
        flex-direction: column;
        row-gap: ${getPurpurSpacing('300')};
        border-width: ${getPurpurBorder('widthXs')};
        border-radius: ${getPurpurBorder('radiusLg')};
        border-style: solid;
        border-color: ${$negative ? $backgroundColor : getPurpurColor('borderWeak')};
        background: ${$backgroundColor};
        color: ${$negative ? getPurpurColor('textDefaultNegative') : getPurpurColor('textDefault')};

        ${CardCap} + & {
            border-top-color: ${$negative ? getPurpurColor('borderMediumNegative') : $backgroundColor};
            border-radius: 0 0 ${getPurpurBorder('radiusLg')} ${getPurpurBorder('radiusLg')};
        }

        ${purpurMedia('sm')`
            padding: ${getPurpurSpacing('300')} ${getPurpurSpacing('200')};
        `}
        ${purpurMedia('md')`
            padding: ${getPurpurSpacing('300')};
        `}
    `;
});

export const SubTitleHeading = styled.h3<NegativeAttr>(
    ({ $negative }) => css`
        margin: 0;
        color: ${$negative ? getPurpurColor('textMediumNegative') : getPurpurColor('textMedium')};
        font-size: ${getPurpurTypography('scale75')};
        font-weight: ${getPurpurTypography('weightNormal')};
        line-height: ${getPurpurTypography('lineHeightDefault')};
    `,
);

export const CardBadge = styled.span<NegativeAttr & { $variant: 'attention' | 'neutral' }>(
    ({ $negative, $variant }) => css`
        padding: ${getPurpurSpacing('100')};
        border-width: ${getPurpurBorder('widthSm')};
        border-style: solid;
        border-radius: ${getPurpurBorder('radiusMd')};
        color: ${
            $negative
                ? getPurpurColor('functionalWhite')
                : ($variant === 'attention' && getPurpurColor('textStatusSpecial')) ||
                  ($variant === 'neutral' && getPurpurColor('textStatusNeutral')) ||
                  getPurpurColor('textStatusNeutral')
        };
        border-color: ${
            $negative
                ? getPurpurColor('functionalWhite')
                : ($variant === 'attention' && getPurpurColor('borderInteractivePrimary')) ||
                  ($variant === 'neutral' && getPurpurColor('borderStrong')) ||
                  getPurpurColor('borderStrong')
        };

        text-align: center;
        line-height: 1;
        font-size: ${getPurpurTypography('scale100')};
        font-weight: ${getPurpurTypography('weightBold')};
    `,
);

export const TitleHeading = styled.div<NegativeAttr>(
    ({ $negative }) => css`
        margin: 0;
        color: ${$negative ? getPurpurColor('textDefaultNegative') : getPurpurColor('textDefault')};
        font-size: ${getPurpurTypography('scale700')};
        font-weight: ${getPurpurTypography('weightBold')};
        line-height: ${getPurpurTypography('lineHeightDefault')};
        display: flex;
        align-items: center;
        gap: ${getPurpurSpacing('100')};
        flex-wrap: wrap;
    `,
);

export const OldTitleText = styled.span`
    font-size: ${getPurpurTypography('scale100')};
    font-weight: ${getPurpurTypography('weightNormal')};
    line-height: ${getPurpurTypography('lineHeightLooser')};
    text-decoration-line: line-through;
`;

export const Header = styled.div`
    display: grid;
    grid-template-columns: 1fr auto;
    grid-template-rows: auto auto;
    align-items: start;
    gap: ${getPurpurSpacing('100')};
    ${CardBadge} {
        grid-column: 2;
        grid-row: 1 / span 2;
    }
    ${TitleHeading} {
        grid-column: 1;
    }
    ${SubTitleHeading} {
        grid-column: 1;
    }
`;
export const DetailsUList = styled.ul`
    margin: 0;
    padding: 0;
    list-style: none;
    line-height: ${getPurpurTypography('lineHeightDefault')};
    margin-bottom: auto;

    li + li {
        margin-top: ${getPurpurSpacing('100')};
    }
`;

export const PriceTag = styled.span<NegativeAttr & { $variant?: 'attention' | 'neutral' }>(
    ({ $negative, $variant }) => css`
        color: ${match({ negative: $negative, variant: $variant })
            .with({ negative: true }, () => getPurpurColor('functionalWhite'))
            .with({ variant: 'attention' }, () => getPurpurColor('textStatusDiscount'))
            .otherwise(() => getPurpurColor('textDefault'))};

        font-size: ${getPurpurTypography('scale400')};
        font-weight: ${getPurpurTypography('weightBold')};
        line-height: ${getPurpurTypography('lineHeightDefault')};
        white-space: nowrap;
    `,
);
export const OldPriceTag = styled.span<NegativeAttr>`
    font-size: ${getPurpurTypography('scale75')};
    font-weight: ${getPurpurTypography('weightNormal')};
    color: ${({ $negative }) => ($negative ? getPurpurColor('functionalWhite') : getPurpurColor('brandOffBlack'))};
    text-decoration-line: line-through;
    line-height: ${getPurpurTypography('lineHeightLoose')};
`;
export const ContractSummaryWrapper = styled.div``;
export const CTAButton = styled(Button)``;
export const CTAButtonWrapper = styled.div``;

export const PriceSection = styled.div`
    display: grid;
    column-gap: ${getPurpurSpacing('200')};
    grid-template-columns: 1fr auto;

    ${OldPriceTag} {
        grid-column: 1;
        grid-row: 1;
    }

    ${PriceTag} {
        grid-column: 1;
        grid-row: 2;
    }

    ${ContractSummaryWrapper} {
        grid-column: 2;
        grid-row: span 2;
        align-self: end;
    }

    ${CTAButton}, ${CTAButtonWrapper} {
        grid-column: 1/-1;
        grid-row: 3;
        margin-top: ${getPurpurSpacing('200')};
    }
`;

export const Footer = styled.div`
    margin-top: -${getPurpurBorder('radiusLg')};
    position: relative;
    overflow: hidden;

    ${purpurMedia('sm')`
        padding-top: calc(${getPurpurBorder('radiusLg')} + ${getPurpurSpacing('300')});
        padding-left: ${getPurpurSpacing('200')};
        padding-right: ${getPurpurSpacing('200')};
        padding-bottom: ${getPurpurSpacing('300')};
    `}
    ${purpurMedia('md')`
        padding-left: ${getPurpurSpacing('300')};
        padding-right: ${getPurpurSpacing('300')};
    `}
    
    background: ${getPurpurColor('backgroundSecondary')};
    border-width: ${getPurpurBorder('widthXs')};
    border-color: ${getPurpurColor('borderWeak')};
    border-style: none solid solid solid;
    border-radius: 0 0 ${getPurpurBorder('radiusLg')} ${getPurpurBorder('radiusLg')};
`;

const smoothExpansionStyles = (expanded: boolean) => css`
    visibility: ${expanded ? 'visible' : 'collapse'};
    max-height: ${expanded ? 1000 : 0}px; // arbitrary maximum height of expandable area
    transition: 400ms ${expanded ? 'ease-in' : 'ease-out'};
    transition-property: max-height;
`;

export const ExpandableListContainer = styled.div<{ $expanded: boolean }>`
    ${({ $expanded }) => css`
        ${purpurMedia('sm')(() => smoothExpansionStyles($expanded))}
        ${purpurMedia('md')(() => smoothExpansionStyles(true))}
    `};
`;

export const ExpandableList = styled.ul`
    margin: 0;
    padding: ${getPurpurSpacing('200')} 0 0;
    list-style: none;
`;

export const ExpandableListItem = styled.li`
    font-size: ${getPurpurTypography('scale100')};
    font-weight: ${getPurpurTypography('weightNormal')};
    line-height: ${getPurpurTypography('lineHeightLoose')};
    display: flex;
    justify-content: space-between;
    & + & {
        margin-top: ${getPurpurSpacing('100')};
    }
`;

const expandableListTitleStyles = css`
    font-size: ${getPurpurTypography('scale100')};
    font-weight: ${getPurpurTypography('weightBold')};
    line-height: ${getPurpurTypography('lineHeightLoose')};
    margin: 0;
`;

export const ExpandableListTitle = styled.div`
    ${expandableListTitleStyles};
    display: none;
    ${purpurMedia('md')`display: block;`}
`;

export const ExpandButton = styled.button.attrs({ type: 'button' })`
    display: grid;
    grid-template-columns: auto 1fr;
    column-gap: ${getPurpurSpacing('100')};
    width: 100%;
    ${expandableListTitleStyles}

    ${purpurFocus()}
    ${purpurMedia('md')`display: none;`}
`;

export const ExpandIcon = styled(Icon).attrs({ size: 'md' })`
    max-width: ${getPurpurSpacing('300')};
    max-height: ${getPurpurSpacing('300')};
    padding: 0 !important;
    line-height: 1;
    margin-left: auto;
    color: ${getPurpurColor('brandOffBlack')};
`;

export const ExpandableListItemDetail = styled.div`
    text-align: right;
    padding-left: ${getPurpurSpacing('100')};
`;

export const TextButtonWithIconStyled = styled(TextButtonWithIcon)<NegativeAttr>`
    a svg {
        color: ${({ $negative }) =>
            $negative ? getPurpurColor('functionalWhite') : getPurpurColor('textStatusSuccessMedium')};
    }

    a {
        text-decoration: none;
        gap: ${getPurpurSpacing('100')};
    }
`;

export const linkUnderlineStyles = (negative: boolean) => css`
    text-decoration-line: underline;
    text-underline-position: from-font;
    text-decoration-color: ${negative ? getPurpurColor('borderStrongNegative') : getPurpurColor('borderStrong')};
    text-decoration-style: dashed;
    text-decoration-thickness: ${getPurpurBorder('widthXs')};
`;

export const ModalTriggerText = styled.span<NegativeAttr>`
    color: ${({ $negative }) => ($negative ? getPurpurColor('textDefaultNegative') : getPurpurColor('textDefault'))};
    ${({ $negative }) => linkUnderlineStyles($negative)};
`;

export const TextWithIconStyled = styled.span<NegativeAttr>`
    display: inline-flex;
    align-items: center;
    gap: ${getPurpurSpacing('100')};

    color: ${({ $negative }) => ($negative ? getPurpurColor('textMediumNegative') : getPurpurColor('textMedium'))};

    > [class^='_purpur-icon_'] {
        flex-shrink: 0;
        color: ${({ $negative }) => ($negative ? getPurpurColor('textMediumNegative') : getPurpurColor('textMedium'))};
    }
`;

export const ModalTriggerTextButton = styled(TextButton)`
    a {
        color: ${getPurpurColor('textDefault')};
        ${linkUnderlineStyles(false)};
    }
`;

export const SmallerLink = styled(Link)<Partial<LinkProps>>`
    font-size: ${getPurpurTypography('scale75')};
    a {
        ${({ negative }) => linkUnderlineStyles(negative)};
    }
`;
